import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Phone, Search, ChevronRight, Clock, CheckCircle, XCircle, Zap, Voicemail } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import CallRecordingButton from '../components/CallRecordingButton';
import WonPill from '../components/WonPill';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const formatCallType = (callType) => {
  if (!callType) return '';
  return callType
    .split('_')
    .map(word => {
      const upper = word.toUpperCase();
      if (['IT', 'MSP', 'BDM', 'B2B', 'HR', 'CRM'].includes(upper)) return upper;
      return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
    })
    .join(' ');
};

// Full Australian phone normalization
const normalizeAuPhone = (raw) => {
  if (!raw) return '—';
  let n = raw;
  try { n = decodeURIComponent(n); } catch (e) { console.debug('normalizeAuPhone: input was not URI-encoded, using raw', e?.message); /* keep raw */ }
  if (n.includes('@')) n = n.split('@')[0];
  if (!n) return '—';
  if (/^\d{1,6}$/.test(n)) return n;            // short extensions: leave as-is
  if (n.startsWith('+61') && n.length >= 12) return '0' + n.slice(3);
  if (/^61\d{9}$/.test(n)) return '0' + n.slice(2);  // 61xxxxxxxxx → 0xxxxxxxxx
  if (/^[23478]\d{8}$/.test(n)) return '0' + n;       // 9-digit missing leading 0
  return n;
};

// Display agent as "205 Phil Hill" or "Ext 205"
const formatAgent = (agentId, agentName) => {
  if (!agentName || agentName === agentId) return `Ext ${agentId}`;
  if (/^[\d+\-() ]{5,}$/.test(agentName)) return `Ext ${agentId}`;
  return `${agentId} ${agentName}`;
};

const StatusBadge = ({ status }) => {
  const cfg = {
    analyzed: { cls: 'bg-emerald-100 text-emerald-700', icon: CheckCircle },
    escalated: { cls: 'bg-red-100 text-red-700', icon: XCircle },
    pending: { cls: 'bg-amber-100 text-amber-700', icon: Clock },
    skipped_short: { cls: 'bg-slate-100 text-slate-500', icon: Clock },
  }[status] || { cls: 'bg-slate-100 text-slate-500', icon: Clock };
  const Icon = cfg.icon;
  return (
    <span className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full font-medium ${cfg.cls}`}>
      <Icon className="w-3 h-3" />{status === 'skipped_short' ? 'short' : status}
    </span>
  );
};

const CallsListPage = () => {
  const { userIndustry } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const [calls, setCalls] = useState([]);
  const [filteredCalls, setFilteredCalls] = useState([]);
  const [loading, setLoading] = useState(true);
  const [knowledgeBases, setKnowledgeBases] = useState([]);
  // `{call_id: N}` map of VoiceFabric action counts, populated as a
  // single bulk POST after the main /calls fetch. Powers the amber
  // ⚡N chip in the agent name cell.
  const [vfCounts, setVfCounts] = useState({});

  // Initialize filters from URL params to preserve state when navigating back
  const [searchTerm, setSearchTerm] = useState(searchParams.get('search') || '');
  const [filterType, setFilterType] = useState(searchParams.get('type') || 'all');
  const [filterStatus, setFilterStatus] = useState(searchParams.get('status') || 'all');
  const [hasViolation, setHasViolation] = useState(searchParams.get('has_violation') === '1');
  const [excludeVoicemail, setExcludeVoicemail] = useState(searchParams.get('exclude_voicemail') === '1');
  const [weekFilter, setWeekFilter] = useState(searchParams.get('week') || '');
  // Master-admin scope override — populated when we arrived from the
  // Grading Pipeline drill-in sparkline for a specific tenant. Ignored
  // by non-master users because their /calls response is already
  // domain-scoped server-side.
  const [domainFilter, setDomainFilter] = useState(searchParams.get('domain_id') || '');

  const fetchCalls = useCallback(async () => {
    try {
      // Compose the /calls request with any deep-link filters that
      // narrow server-side — has_violation and week both cross-reference
      // other collections so client-side filtering wouldn't work.
      const p = new URLSearchParams({ limit: '200' });
      if (hasViolation) p.set('has_violation', '1');
      if (excludeVoicemail) p.set('exclude_voicemail', '1');
      if (weekFilter) p.set('week', weekFilter);
      if (domainFilter) p.set('domain_id', domainFilter);
      const response = await axios.get(`${API}/calls?${p.toString()}`);
      const filtered = userIndustry
        ? response.data.filter(call => call.call_type === userIndustry)
        : response.data;
      setCalls(filtered);
      // Bulk VoiceFabric-count lookup — non-blocking, fails quiet.
      // If the endpoint 502s or 403s the table still renders, just
      // without the ⚡N chip.
      try {
        const ids = filtered.map(c => c.call_id).filter(Boolean);
        if (ids.length) {
          const vfRes = await axios.post(`${API}/calls/voicefabric-counts`, { call_ids: ids });
          setVfCounts(vfRes.data?.counts || {});
        } else {
          setVfCounts({});
        }
      } catch (e) {
        console.debug('voicefabric-counts fetch failed', e?.message);
        setVfCounts({});
      }
    } catch (error) {
      console.error('Failed to load calls', error);
      toast.error('Failed to load calls');
    } finally {
      setLoading(false);
    }
  }, [userIndustry, hasViolation, excludeVoicemail, weekFilter, domainFilter]);

  const fetchKnowledgeBases = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/knowledge-bases`);
      const filtered = userIndustry
        ? response.data.filter(kb => kb.call_type === userIndustry)
        : response.data;
      setKnowledgeBases(filtered);
    } catch (error) {
      console.error('Failed to load knowledge bases', error);
    }
  }, [userIndustry]);

  const filterCalls = useCallback(() => {
    let filtered = calls;
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(call =>
        (call.agent_name || '').toLowerCase().includes(term) ||
        (call.customer_name || '').toLowerCase().includes(term) ||
        (call.customer_phone || '').includes(searchTerm) ||
        call.call_id.toLowerCase().includes(term)
      );
    }
    if (filterType !== 'all') {
      filtered = filtered.filter(call => call.call_type === filterType);
    }
    if (filterStatus !== 'all') {
      filtered = filtered.filter(call => call.status === filterStatus);
    }
    if (hasViolation) {
      // Server has already narrowed to violation-only calls; client-side
      // pass-through here so the "Compliance filter active" pill can
      // reflect the count and a "Clear filter" pill will show up.
      filtered = filtered.filter(Boolean);
    }
    setFilteredCalls(filtered);
  }, [calls, searchTerm, filterType, filterStatus, hasViolation]);

  useEffect(() => {
    fetchCalls();
    fetchKnowledgeBases();
  }, [fetchCalls, fetchKnowledgeBases, hasViolation, weekFilter, domainFilter]);

  useEffect(() => {
    filterCalls();
  }, [filterCalls]);

  // Sync filters to URL so they survive navigation (Back button restores them)
  useEffect(() => {
    const params = {};
    if (searchTerm) params.search = searchTerm;
    if (filterType !== 'all') params.type = filterType;
    if (filterStatus !== 'all') params.status = filterStatus;
    if (hasViolation) params.has_violation = '1';
    if (excludeVoicemail) params.exclude_voicemail = '1';
    if (weekFilter) params.week = weekFilter;
    if (domainFilter) params.domain_id = domainFilter;
    setSearchParams(params, { replace: true });
  }, [searchTerm, filterType, filterStatus, hasViolation, excludeVoicemail, weekFilter, domainFilter, setSearchParams]);

  const getFromNumber = (call) =>
    normalizeAuPhone(
      call.call_direction === 'inbound'
        ? call.customer_phone || '—'
        : call.agent_id || '—'
    );

  const getToNumber = (call) =>
    normalizeAuPhone(
      call.call_direction === 'inbound'
        ? call.agent_id || '—'
        : call.customer_phone || '—'
    );

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="calls-list-page">
      <div>
        <h1 className="text-4xl font-bold text-slate-900 mb-2" style={{ fontFamily: 'Outfit' }}>
          Calls
        </h1>
        <p className="text-slate-600">
          {filteredCalls.length} call{filteredCalls.length !== 1 ? 's' : ''} found
        </p>
      </div>

      {/* Filters */}
      <Card className="border-slate-200">
        <CardContent className="pt-6">
          {/* Active deep-link filter chips — dismissible. Populated when
              the user arrives here from Compliance Trend or another
              drill-down that supplies has_violation/week. */}
          {(hasViolation || weekFilter || domainFilter) && (
            <div className="flex flex-wrap gap-2 mb-4" data-testid="calls-active-filters">
              {hasViolation && (
                <span className="inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-full bg-rose-100 text-rose-800 border border-rose-200" data-testid="calls-filter-pill-violation">
                  Compliance violations only
                  <button
                    type="button"
                    onClick={() => setHasViolation(false)}
                    className="hover:text-rose-900"
                    aria-label="Clear violations filter"
                  >×</button>
                </span>
              )}
              {weekFilter && (
                <span className="inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-full bg-indigo-100 text-indigo-800 border border-indigo-200" data-testid="calls-filter-pill-week">
                  Week {weekFilter}
                  <button
                    type="button"
                    onClick={() => setWeekFilter('')}
                    className="hover:text-indigo-900"
                    aria-label="Clear week filter"
                  >×</button>
                </span>
              )}
              {domainFilter && (
                <span className="inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-full bg-teal-100 text-teal-800 border border-teal-200" data-testid="calls-filter-pill-domain">
                  Single tenant
                  <button
                    type="button"
                    onClick={() => setDomainFilter('')}
                    className="hover:text-teal-900"
                    aria-label="Clear domain filter"
                  >×</button>
                </span>
              )}
            </div>
          )}
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-4 h-4" />
              <Input
                placeholder="Search by agent, number, or call ID..."
                className="pl-10"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                data-testid="search-input"
              />
            </div>
            <select
              className="px-4 py-2 border border-slate-200 rounded-lg text-sm"
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
              data-testid="filter-type-select"
            >
              <option value="all">All Types</option>
              {knowledgeBases.map(kb => (
                <option key={kb.id} value={kb.call_type}>{kb.name}</option>
              ))}
            </select>
            <select
              className="px-4 py-2 border border-slate-200 rounded-lg text-sm"
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              data-testid="filter-status-select"
            >
              <option value="all">All Status</option>
              <option value="pending">Pending</option>
              <option value="analyzed">Analyzed</option>
              <option value="escalated">Escalated</option>
            </select>
            <label
              className="inline-flex items-center gap-1.5 px-3 py-2 border border-slate-200 rounded-lg cursor-pointer bg-white hover:bg-slate-50"
              data-testid="calls-toggle-voicemail"
              title="Hide calls detected as voicemail greetings or voicemail-screened."
            >
              <input
                type="checkbox"
                checked={excludeVoicemail}
                onChange={(e) => setExcludeVoicemail(e.target.checked)}
                className="w-3.5 h-3.5 rounded border-slate-300 text-teal-600 focus:ring-teal-400"
              />
              <span className="text-xs font-semibold text-slate-700">Hide voicemails</span>
            </label>
          </div>
        </CardContent>
      </Card>

      {/* Calls Table */}
      <Card className="border-slate-200">
        {filteredCalls.length === 0 ? (
          <CardContent className="py-12 text-center">
            <Phone className="w-12 h-12 mx-auto mb-4 text-slate-300" />
            <h3 className="text-lg font-medium text-slate-900 mb-2">No calls found</h3>
            <p className="text-slate-500">Try adjusting your filters</p>
          </CardContent>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-200 bg-slate-50">
                  {['Agent', 'From', 'To', 'Time', 'Date', 'Type', 'Duration', 'Status', ''].map(h => (
                    <th key={h} className="text-left text-slate-500 text-xs font-semibold px-4 py-3 uppercase tracking-wide">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filteredCalls.map(call => (
                  <tr
                    key={call.call_id}
                    className="border-b border-slate-100 hover:bg-slate-50 transition-colors cursor-pointer"
                    data-testid={`call-row-${call.call_id}`}
                    onClick={() => navigate(`/calls/${call.call_id}`)}
                  >
                    <td className="px-4 py-3 font-medium text-slate-900">
                      <div className="flex items-center gap-2">
                        <span>{formatAgent(call.agent_id, call.agent_name, call.extension)}</span>
                        {(call.is_voicemail || call.is_voicemail_screened) && (
                          <span
                            className="inline-flex items-center gap-0.5 text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-purple-100 text-purple-700"
                            title={call.is_voicemail_screened ? 'Voicemail screened (hit customer VM)' : 'Voicemail greeting'}
                            data-testid={`call-vm-chip-${call.call_id}`}
                          >
                            <Voicemail className="w-2.5 h-2.5" />
                            {call.is_voicemail_screened ? 'VM-scr' : 'VM'}
                          </span>
                        )}
                        {vfCounts[call.call_id] > 0 && (
                          <Link
                            to={`/calls/${call.call_id}/voicefabric`}
                            className="inline-flex items-center gap-0.5 text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700 hover:bg-amber-200 transition-colors"
                            title={`${vfCounts[call.call_id]} VoiceFabric action${vfCounts[call.call_id] === 1 ? '' : 's'} fired — click to view trace`}
                            data-testid={`tenant-vf-chip-${call.call_id}`}
                            onClick={(e) => e.stopPropagation()}
                          >
                            <Zap className="w-2.5 h-2.5" /> {vfCounts[call.call_id]}
                          </Link>
                        )}
                        {call.won && (
                          <WonPill source={call.chase_outcome_source} by={call.chase_outcome_by} />
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-slate-600 font-mono text-xs">
                      {getFromNumber(call)}
                    </td>
                    <td className="px-4 py-3 text-slate-600 font-mono text-xs">
                      {getToNumber(call)}
                    </td>
                    <td className="px-4 py-3 text-slate-500 text-xs whitespace-nowrap">
                      {new Date(call.call_date).toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit' })}
                    </td>
                    <td className="px-4 py-3 text-slate-500 text-xs whitespace-nowrap">
                      {new Date(call.call_date).toLocaleDateString('en-AU', { day: '2-digit', month: 'short', year: '2-digit' })}
                    </td>
                    <td className="px-4 py-3 text-slate-600">
                      {formatCallType(call.call_type)}
                    </td>
                    <td className="px-4 py-3 text-slate-500">
                      {Math.floor(call.duration_seconds / 60)}m {call.duration_seconds % 60}s
                    </td>
                    <td className="px-4 py-3">
                      <StatusBadge status={call.status} />
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <CallRecordingButton callId={call.call_id} size="xs" />
                        <Link
                          to={`/calls/${call.call_id}`}
                          className="text-primary hover:text-primary/80 transition-colors"
                          data-testid={`call-link-${call.call_id}`}
                        >
                          <ChevronRight className="w-4 h-4" />
                        </Link>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
};

export default CallsListPage;
